import { User, Bot, FileText, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { useState } from 'react';
import type { ChatMessage, SourceCitation } from '../../types';

interface MessageBubbleProps {
  message: ChatMessage;
  onSourceClick?: (source: SourceCitation) => void;
}

export function MessageBubble({ message, onSourceClick }: MessageBubbleProps) {
  const [sourcesExpanded, setSourcesExpanded] = useState(false); // Collapsed by default
  const isUser = message.role === 'user';
  const hasSources = message.sources && message.sources.length > 0;
  const hasProtocolContext = !isUser && message.protocolContext;

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-primary-600">
        {isUser ? (
          <User size={16} className="text-white" />
        ) : (
          <Bot size={16} className="text-white" />
        )}
      </div>

      {/* Message content */}
      <div className={`flex-1 max-w-[85%] ${isUser ? 'text-right' : ''}`}>
        {/* Protocol context banner */}
        {hasProtocolContext && (
          <div className="mb-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs bg-primary-50 border border-primary-200 dark:bg-primary-900/20 dark:border-primary-800">
            <MapPin size={12} className="text-primary-600 dark:text-primary-400" />
            <span className="text-primary-700 dark:text-primary-300">
              {message.protocolContext!.matched_from_message && (
                <span className="text-primary-500 mr-1 dark:text-primary-400">Detected:</span>
              )}
              <span className="font-medium">{message.protocolContext!.protocol_number}</span>
              {message.protocolContext!.site_name && (
                <span className="text-primary-500 dark:text-primary-400"> at {message.protocolContext!.site_name}</span>
              )}
            </span>
          </div>
        )}

        <div
          className={`inline-block px-4 py-3 rounded-2xl ${
            isUser
              ? 'bg-primary-600 text-white rounded-tr-sm'
              : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100'
          }`}
        >
          {message.isLoading ? (
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Thinking...</span>
            </div>
          ) : (
            <div className="whitespace-pre-wrap text-sm leading-relaxed">
              {message.content}
            </div>
          )}
        </div>

        {/* Sources */}
        {hasSources && !message.isLoading && (
          <div className="mt-2">
            <button
              onClick={() => setSourcesExpanded(!sourcesExpanded)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors duration-150 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FileText size={12} />
              <span>{message.sources!.length} source{message.sources!.length > 1 ? 's' : ''}</span>
              {sourcesExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>

            {sourcesExpanded && (
              <div className="mt-2 space-y-2">
                {message.sources!.map((source, index) => (
                  <SourceCard key={index} source={source} onClick={onSourceClick} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Timestamp */}
        <div className={`text-xs text-gray-400 mt-1 dark:text-gray-500 ${isUser ? 'text-right' : ''}`}>
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>

      </div>
    </div>
  );
}

interface SourceCardProps {
  source: SourceCitation;
  onClick?: (source: SourceCitation) => void;
}

function SourceCard({ source, onClick }: SourceCardProps) {
  const isClickable = onClick !== undefined;

  return (
    <button
      type="button"
      onClick={() => onClick?.(source)}
      disabled={!isClickable}
      className={`
        w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-left dark:bg-gray-800 dark:border-gray-700
        ${isClickable ? 'cursor-pointer hover:bg-primary-50 hover:border-primary-300 transition-colors duration-150 dark:hover:bg-primary-900/20 dark:hover:border-primary-700' : ''}
      `}
    >
      <div className="flex items-start gap-2">
        <FileText size={14} className="text-primary-600 mt-0.5 flex-shrink-0 dark:text-primary-400" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-100">
              {source.document_title}
            </p>
            {source.document_type && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 uppercase dark:bg-primary-900/40 dark:text-primary-300">
                {source.document_type}
              </span>
            )}
            {isClickable && (
              <span className="text-[10px] text-primary-600 ml-auto dark:text-primary-400">
                Click to view
              </span>
            )}
          </div>
          {source.page_number && (
            <p className="text-xs text-gray-500 mt-0.5 dark:text-gray-400">
              Page {source.page_number}
            </p>
          )}
          <p className="text-xs text-gray-600 mt-1 line-clamp-2 dark:text-gray-300">
            {source.content_preview}
          </p>
        </div>
      </div>
    </button>
  );
}
